import {
  Body,
  Link,
  Stack,
  useModals,
  useWindowProperties,
} from '@qred/components-library';
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { useHistory } from 'react-router-dom';
import PDFViewer from '~/components/shared/PDFViewer/PDFViewer';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import constVars from '~/constants/constVars';
import {
  companyCreditCheckAgencies,
  getMarketHasCompanyCreditCheck,
} from '~/constants/markets';
import { StepStatus } from '~/enums';
import { areSameCompanies } from '~/helpers/utils';
import useTranslate from '~/hooks/useTranslate';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import {
  getLoanApplication,
  patchApplication,
  postApplication,
} from '~/store/actions/loanApplicationActions';
import { ApiStatus } from '~/store/types/sharedTypes';
import { RootState } from '~/store';

const CompanySelectorFooter = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { formatList } = useIntl();

  const {
    loanApplication: {
      form,
      apiStatus: applicationApiStatus,
      currentApplicationOrganizationIdentifier,
      overview,
    },
    intl: { market },
  } = useSelector((state: RootState) => state);
  const { isMobile } = useWindowProperties();

  const t = useTranslate();
  const modal = useModals();

  const applicationApiRequestInProgress = [
    applicationApiStatus.application,
    applicationApiStatus.patch,
    applicationApiStatus.post,
  ].some((apiStatus) => apiStatus === ApiStatus.Started);

  const onNextStepClick = useCallback(async () => {
    const selectedCompany = overview.clients.find((client) =>
      areSameCompanies(
        form.organizationNumber || form.connectId,
        client.company_no || client.connect_id
      )
    );

    try {
      dispatch(pushToGtmLoanAction({ actionName: 'next_button_pressed' }));
      // TODO: if user comes back to step 1 and doesnt change company and clicks next, we should PATCH not GET
      if (selectedCompany?.has_offer_to_sign) {
        dispatch(pushToGtmLoanAction({ actionName: 'redirect_to_offer_page' }));
        history.push(
          selectedCompany.application_uuid
            ? `/${constVars.ROUTE_LOAN_OFFER}/${selectedCompany.application_uuid}`
            : `/offer/${selectedCompany.client_id}`
        );
        return;
      }

      const companyHasChanged = !areSameCompanies(
        form.organizationNumber || form.connectId,
        currentApplicationOrganizationIdentifier
      );

      if (
        selectedCompany?.application_uuid &&
        selectedCompany?.has_inprogress_application &&
        companyHasChanged
      ) {
        dispatch(
          pushToGtmLoanAction({
            actionName: 'active_application_continue',
          })
        );

        const currentStepFinished = true;
        await dispatch(
          getLoanApplication(
            selectedCompany.application_uuid,
            currentStepFinished
          )
        );
        await dispatch(patchApplication({ currentStepCompleted: false }));
      } else {
        if (companyHasChanged) {
          await dispatch(postApplication());
        } else {
          await dispatch(patchApplication());
        }
      }
    } catch (_err: any) {
      console.log(_err);

      dispatch(
        pushToGtmLoanAction({
          stepStatus: StepStatus.Error,
          errorMessage: _err.response?.data?.message,
        })
      );
    }
  }, [
    currentApplicationOrganizationIdentifier,
    dispatch,
    form.organizationNumber,
    form.connectId,
    history,
    overview.clients,
  ]);

  const openPdfViewerModal = (fileSrc: string) => {
    modal.openModal({
      size: isMobile ? 'md' : 'xl',
      children: <PDFViewer file={fileSrc} />,
    });
  };

  const agencies = companyCreditCheckAgencies[market];

  return (
    <Stack w={isMobile ? '100%' : 'auto'}>
      {getMarketHasCompanyCreditCheck(market) && (
        <Body
          data-testid="company-selector-footer-disclaimer-container"
          color="textDark.200"
          size="sm"
          data-record
        >
          {t('Onboarding.CompanyCreditConsent', {
            agencies: formatList(
              agencies.map((agency) => {
                return (
                  <Link
                    data-record
                    key={agency.id}
                    onClick={() => openPdfViewerModal(agency.pdfPath)}
                    size="sm"
                  >
                    {agency.label}
                  </Link>
                );
              })
            ),
            next: `"${t('Onboarding.Next')}"`,
          })}
        </Body>
      )}

      <NextButton
        label="Onboarding.Next"
        isLoading={applicationApiRequestInProgress}
        action={onNextStepClick}
      />
    </Stack>
  );
};

export default CompanySelectorFooter;
